import React, { useMemo } from "react";
import type { Meta, StoryObj } from "@storybook/react";
import { createNativeStackNavigator } from "@react-navigation/native-stack";

import { MockedProvider } from "@apollo/client/testing/react";
import { GET_ASSET } from "../../graphql/asset";
import { buildAsset, buildPayslip, buildSalary } from "../factories";
import SalaryDetailsScreen from "../../screens/worker/SalaryDetails";
import { FIND_MY_SALARIES, GET_PAYSLIP, GET_SALARY } from "../../graphql/salaries";
import { StoryStackNavigatorContainer } from "../display-helpers";
import Salaries from "../../screens/worker/Salaries";
import SalaryPayslips from "../../screens/worker/SalaryPayslips";
import PayslipDetailsScreen from "../../screens/worker/PayslipDetails";
import { createApolloCache } from "../../configs/graphql.config";

const Stack = createNativeStackNavigator<{
    SalariesScreen: undefined;
    SalaryDetails: { salaryId: string };
    SalaryPayslips: { salaryId: string };
    PayslipDetails: { payslipId: string };
}>();

const StoryComponent = () => {
    const salary007 = useMemo(() => {
        return buildSalary({
            id: "007",
            year: 2025,
            month: 1,
            yearMonth: "2025-01",
            paidAt: "2025-02-10",
            netAmount: 250000,
            salaryAsset: buildAsset({
                id: "asset-01",
            }),
        });
    }, []);
    const payslip1 = buildPayslip({
        id: "ps-008-a",
        asset: buildAsset({
            id: "asset-02",
        }),
    });
    const payslip2 = buildPayslip({
        id: "ps-008-b",
        asset: buildAsset({
            id: "asset-03",
        }),
    });
    const salary008 = useMemo(() => {
        return buildSalary({
            id: "008",
            year: 2025,
            month: 2,
            yearMonth: "2025-02",
            paidAt: null,
            netAmount: 250000,
            salaryAsset: null,
            payslips: [payslip1, payslip2],
        });
    }, [payslip1, payslip2]);
    const mocks = useMemo(
        () => [
            {
                request: {
                    query: FIND_MY_SALARIES,
                    variables: () => true,
                },
                maxUsageCount: Number.POSITIVE_INFINITY,
                result: () => ({
                    data: {
                        findMySalaries: {
                            count: 2,
                            elements: [salary007, salary008],
                            pageInfo: {
                                __typename: "PageInfo",
                                currentOffset: 0,
                                limit: 12,
                                hasNextPage: false,
                                hasPreviousPage: false,
                                nextOffset: null,
                                previousOffset: null,
                            },
                        },
                    },
                }),
            },
            {
                request: {
                    query: GET_SALARY,
                    variables: {
                        salaryId: "007",
                    },
                },
                maxUsageCount: Number.POSITIVE_INFINITY,
                result: () => ({
                    data: {
                        getSalary: salary007,
                    },
                }),
            },
            {
                request: {
                    query: GET_SALARY,
                    variables: {
                        salaryId: "008",
                    },
                },
                maxUsageCount: Number.POSITIVE_INFINITY,
                result: () => ({
                    data: {
                        getSalary: salary008,
                    },
                }),
            },
            {
                request: {
                    query: GET_PAYSLIP,
                    variables: {
                        payslipId: "ps-008-a",
                    },
                },
                maxUsageCount: Number.POSITIVE_INFINITY,
                result: () => ({
                    data: {
                        getPayslip: payslip1,
                    },
                }),
            },
            {
                request: {
                    query: GET_PAYSLIP,
                    variables: {
                        payslipId: "ps-008-b",
                    },
                },
                maxUsageCount: Number.POSITIVE_INFINITY,
                result: () => ({
                    data: {
                        getPayslip: payslip2,
                    },
                }),
            },
            {
                request: {
                    query: GET_ASSET,
                    variables: {
                        assetId: "asset-01",
                    },
                },
                maxUsageCount: Number.POSITIVE_INFINITY,
                result: () => {
                    return {
                        data: {
                            getAsset: buildAsset({ id: "asset-01" }),
                        },
                    };
                },
            },
            {
                request: {
                    query: GET_ASSET,
                    variables: {
                        assetId: "asset-02",
                    },
                },
                maxUsageCount: Number.POSITIVE_INFINITY,
                result: () => {
                    return {
                        data: {
                            getAsset: buildAsset({ id: "asset-02" }),
                        },
                    };
                },
            },
            {
                request: {
                    query: GET_ASSET,
                    variables: {
                        assetId: "asset-03",
                    },
                },
                maxUsageCount: Number.POSITIVE_INFINITY,
                result: () => {
                    return {
                        data: {
                            getAsset: buildAsset({ id: "asset-03" }),
                        },
                    };
                },
            },
        ],
        [payslip1, payslip2, salary007, salary008],
    );
    return (
        <MockedProvider mocks={mocks} cache={createApolloCache()}>
            <StoryStackNavigatorContainer>
                <Stack.Navigator initialRouteName="SalariesScreen">
                    <Stack.Screen
                        name="SalariesScreen"
                        component={Salaries}
                        options={{ headerShown: true, title: "List des salaires" }}
                    />
                    <Stack.Screen
                        name="SalaryDetails"
                        component={SalaryDetailsScreen}
                        options={{ headerShown: true, title: "Fiche de salaire" }}
                    />
                    <Stack.Screen
                        name="SalaryPayslips"
                        component={SalaryPayslips}
                        options={{ headerShown: true, title: "Fiches de salaire" }}
                    />
                    <Stack.Screen
                        name="PayslipDetails"
                        component={PayslipDetailsScreen}
                        options={{ headerShown: true, title: "Fiche de salaire" }}
                    />
                </Stack.Navigator>
            </StoryStackNavigatorContainer>
        </MockedProvider>
    );
};

const meta = {
    title: "Screens/Salaries",
    component: StoryComponent,
    argTypes: {},
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Default: Story = {};
